/* eslint new-cap: ["error", { "capIsNew": false }]*/
import { Set, Map, fromJS } from 'immutable';
import { RestAPI } from '@/util/net.js';
import { putError } from '@/store/error.store';
import { VERSIONS, CURRENT_RELEASE } from '@/util/version';
import { hasUserAnyRole } from '../../../auth/RoleConfigFilter.jsx';
import store from '../../../../store/store';


const isAdminOrManager = () => {
  const roles = store.getState().getIn(['session', 'roles'], '');
  return roles.includes('ADMIN') || roles.includes('MANAGEMENT');
};

function setTypeFilter(state, typeName, typeValues) {
  return typeValues.reduce((newState, typeValue) => {
    if (!newState.getIn(['filters', typeName], new Set()).has(typeValue)) {
      return newState.setIn(['filters', typeName],
        newState.getIn(['filters', typeName], new Set()).add(typeValue));
    }
    return newState.setIn(['filters', typeName],
      newState.getIn(['filters', typeName], new Set()).delete(typeValue));
  }, state);
}
const setProdTypeFilter = (state, pTypes) => setTypeFilter(state, 'ptypes', pTypes);

const setTransportTypeFilter = (state, tTypes) => setTypeFilter(state, 'ttypes', tTypes);


const setMyDealsFilter = (state, value) =>
  state.setIn(['filters', 'myDeals'], value).setIn(['filters', 'operationUserId'], null);

const setIsArchivedFilter = (state, value) => state.setIn(['filters', 'isarchived'], value)
  .deleteIn(['filters', 'dispute']).deleteIn(['filters', 'dealCanceled']);

const setHasDisputeFilter = (state, value) => state.setIn(['filters', 'dispute'], value)
  .deleteIn(['filters', 'isarchived']).deleteIn(['filters', 'dealCanceled']);

const setDealCanceledFilter = (state, value) => state.setIn(['filters', 'dealCanceled'], value)
  .deleteIn(['filters', 'isarchived']).deleteIn(['filters', 'dispute']);

const setShipmentSort = (state, value) => state.set('shipmentSort', value);

const setUnlinkedFilter = (state, value) => state.setIn(['filters', 'unlinked'], value);

const setOperationUserId = (state, value) =>
  setMyDealsFilter(state, false).setIn(['filters', 'operationUserId'], value);

const recieveFilteredData = (state, data) => state.set('data', fromJS(data))
  .set('dataVersion', state.get('dataVersion', 0) + 1);

const intialState = new Map().setIn(['filters', 'ptypes'], new Set())
  .setIn(['filters', 'ttypes'], new Set())
  .setIn(['filters', 'myDeals'], CURRENT_RELEASE >= VERSIONS.Harmony);

const initMyDealsFilter = (dealType) => {
  const set = CURRENT_RELEASE >= VERSIONS.Harmony;
  if (dealType === 'transport') {
    return !isAdminOrManager();
  }
  return set && hasUserAnyRole(['ROLE_OPERATIONS', 'ROLE_OPERATIONS_LIMITED', 'ROLE_TRADER']);
};

const clearFilters = (state, dealType, exclude) => {
  const excludeValues = {};
  exclude.forEach(v => {
    excludeValues[v] = state.getIn(['filters', v]);
  });
  return state.clear().setIn(['filters', 'myDeals'],
    CURRENT_RELEASE >= VERSIONS.Harmony && (excludeValues.myDeals || initMyDealsFilter(dealType)))
    .setIn(['filters', 'ttypes'], excludeValues.ttypes || new Set())
    .setIn(['filters', 'ptypes'], new Set())
    .setIn(['filters', 'dealListSearch'], '');
};

export const searchReducer = (dealType = '') =>
  (state = intialState, action) => {
    switch (action.type) {
      case 'LOGIN_SUCCESSFUL':
      case 'INIT_MY_DEALS':
        return setMyDealsFilter(state, initMyDealsFilter(dealType, action));
      case `${dealType.toUpperCase()}_CHANGE_PRODUCT_TYPE_FILTERS`:
        return setProdTypeFilter(state, action.pTypes);
      case `${dealType.toUpperCase()}_CHANGE_TRANSPORT_TYPE_FILTERS`:
        return setTransportTypeFilter(state, action.tTypes);
      case `${dealType.toUpperCase()}_CHANGE_MY_DEALS_FILTER`:
        return setMyDealsFilter(state, action.value);
      case `${dealType.toUpperCase()}_CHANGE_IS_ARCHIVED_FILTER`:
        return setIsArchivedFilter(state, action.value);
      case `${dealType.toUpperCase()}_CHANGE_HAS_DISPUTE_FILTER`:
        return setHasDisputeFilter(state, action.value);
      case `${dealType.toUpperCase()}_CHANGE_DEAL_CANCELED_FILTER`:
        return setDealCanceledFilter(state, action.value);
      case `${dealType.toUpperCase()}_FILTED_DATA_RECIEVED`:
        return recieveFilteredData(state, action.data);
      case `${dealType.toUpperCase()}_FILTERS_CLEARED`:
        return clearFilters(state, action.dealType, action.exclude);
      case `${dealType.toUpperCase()}_FILTER_DATA_LOADED`:
        return recieveFilteredData(state, action.data);
      case `${dealType.toUpperCase()}_OPERATION_DEAL_CHANGED`:
        return setOperationUserId(state, action.value);
      case `${dealType.toUpperCase()}_DEAL_LIST_SEARCH_VALUE`:
        return state.setIn(['filters', 'dealListSearch'], action.value);
      case 'WS_RECEIVED_DEAL_CREATED':
        return dealType.toUpperCase() === action.dealType.toUpperCase() ?
          state : state;
      case `${dealType.toUpperCase()}_CHANGE_UNLINKED_FILTER`:
        return setUnlinkedFilter(state, action.value);
      case 'LOGOUT':
        return intialState;
      default:
        return state;
    }
  };

export const initMyDeals = (dispatch) => dispatch({ type: 'INIT_MY_DEALS' });

const initialSortState = setShipmentSort(new Map(), false);

export const sortReducer = (dealType = '') => (state = initialSortState, action) => {
  switch (action.type) {
    case `${dealType.toUpperCase()}_CHANGE_SHIPMENT_SORT`:
      return setShipmentSort(state, action.value);
    case 'LOGOUT':
      return initialSortState;
    default:
      return state;
  }
};

export const filterProductTypes = (dispatch, pTypes, dealType) =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_PRODUCT_TYPE_FILTERS`, pTypes });

export const filterTransportTypes = (dispatch, tTypes, dealType = 'transport') =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_TRANSPORT_TYPE_FILTERS`, tTypes });

export const filterMyDeals = (dispatch, value, dealType) =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_MY_DEALS_FILTER`, value });

export const filterIsArchived = (dispatch, value, dealType) =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_IS_ARCHIVED_FILTER`, value });

export const filterHasDispute = (dispatch, value, dealType) =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_HAS_DISPUTE_FILTER`, value });

export const filterDealCanceled = (dispatch, value) => {
  dispatch({ type: 'PURCHASE_CHANGE_DEAL_CANCELED_FILTER', value });
  dispatch({ type: 'SALE_CHANGE_DEAL_CANCELED_FILTER', value });
};

export const filterUnlinked = (dispatch, value, dealType) =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_UNLINKED_FILTER`, value });

export const selectShipmentSort = (dispatch, value, dealType) =>
  dispatch({ type: `${dealType.toUpperCase()}_CHANGE_SHIPMENT_SORT`, value });

export const resetFilters = (dispatch, dealType, ...exclude) =>
  dispatch({ type: `${dealType.toUpperCase()}_FILTERS_CLEARED`, dealType, exclude });

export const filterOperationDeals = (dispatch, dealType, value) =>
  dispatch({ type: `${dealType.toUpperCase()}_OPERATION_DEAL_CHANGED`, value });

export const updateDealListSearch = (dispatch, dealType, value) => {
  dispatch({ type: `${dealType.toUpperCase()}_DEAL_LIST_SEARCH_VALUE`, value });
};

export const filterData = (dispatch, myDeals, selectedTypes, dealType,
  isArchived, dispute, operationUserId, dealCanceled, dealSearch) => {
  let url = `/search/api/${dealType}/filter?my=${myDeals}`
    + `&ptypes=${selectedTypes.toJS()}&isarchived=${isArchived}&dispute=${dispute
    }&dealCanceled=${dealCanceled}&dealSearch=${dealSearch || ''}`;
  if (operationUserId || operationUserId == 0) {
    url += `&operationUserId=${operationUserId}`;
  }
  dispatch({ type: `${dealType.toUpperCase()}_FILTER_DATA_LOADING` });
  return RestAPI.get(url)
    .then(
      (data) => {
        dispatch({ type: `${dealType.toUpperCase()}_FILTER_DATA_LOADED`, data });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: `${dealType.toUpperCase()}_FILTERING_FAILED` });
        return true;
      },
    );
};

export const filterPaymentsData = (dispatch, archivedPayments) => {
  dispatch({ type: 'PAYMENTS_FILTER_DATA_LOADING' });
  return RestAPI.get(`/search/api/payments/filter?archived=${archivedPayments}&detached=`)
    .then(
      (data) => {
        dispatch({ type: 'PAYMENTS_FILTER_DATA_LOADED', data });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'PAYMENTS_FILTERING_FAILED' });
        return true;
      },
    );
};

export const filterDetachedPaymentsData = (dispatch) => {
  dispatch({ type: 'PAYMENTS_FILTER_DATA_LOADING' });
  return RestAPI.get('/search/api/payments/filter?detached=true&archived=')
    .then(
      (data) => {
        dispatch({ type: 'PAYMENTS_FILTER_DATA_LOADED', data });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'PAYMENTS_FILTERING_FAILED' });
        return true;
      },
    );
};

export const filterTransportData = (dispatch, myDeals, selectedTypes, dealType,
  transportTypes, isArchived, dispute, operationUserId, dealSearch) => {
  dispatch({ type: `${dealType.toUpperCase()}_FILTER_DATA_LOADING` });
  let url = `/search/api/${dealType}/filter?my=${myDeals}&ttypes=${transportTypes
    .toJS()}&ptypes=${selectedTypes.toJS()}&isarchived=${isArchived}&dispute=${dispute
  }&threemonth=${dealType == 'transportBulkPayment'}&dealSearch=${dealSearch || ''}`;
  if (operationUserId || operationUserId == 0) {
    url += `&operationUserId=${operationUserId}`;
  }
  return RestAPI.get(url)
    .then(
      (data) => {
        dispatch({ type: `${dealType.toUpperCase()}_FILTER_DATA_LOADED`, data });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: `${dealType.toUpperCase()}_FILTERING_FAILED` });
        return true;
      },
    );
};
